import React, { useEffect, useState, useRef } from 'react';
import { Footer } from '../Footer';
import { SearchIcon, PaperclipIcon, SendIcon, UserIcon, FileIcon, ImageIcon, FileTextIcon, MusicIcon, VideoIcon, UserPlusIcon, CheckCircleIcon, MessageSquareIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Conversation, Message, getUserConversations, subscribeToUserConversations, subscribeToConversationMessages, sendMessage, markMessagesAsRead, debugCheckFirestoreConversations } from '../../services/messagingService';
import { AddStudentModal } from '../modals/AddStudentModal';
import { isStudentAddedToTeacher } from '../../services/studentService';
import { useTheme } from '../providers/ThemeProvider';
export const TeacherMessages = () => {
  const {
    currentUser,
    userData
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // New state for Add Student modal
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
    avatar?: string;
    email?: string;
  } | null>(null);
  const [studentsAddedMap, setStudentsAddedMap] = useState<Record<string, boolean>>({});
  // Add refs for message scrolling
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // DIAGNOSTIC: Log teacher user information when component mounts
  useEffect(() => {
    if (currentUser) {
      console.group('🔍 DIAGNOSTIC: Teacher User Information');
      console.log('Teacher Auth UID:', currentUser.uid);
      console.log('Teacher Email:', currentUser.email);
      console.log('Teacher Display Name:', currentUser.displayName);
      console.log('Teacher User Data from Context:', userData);
      console.groupEnd();
      // DIAGNOSTIC: Check Firestore for conversations related to this teacher
      debugCheckFirestoreConversations(currentUser.uid);
    } else {
      console.warn('🔍 DIAGNOSTIC: No authenticated teacher user found');
    }
  }, [currentUser, userData]);
  // Load conversations when component mounts
  useEffect(() => {
    if (!currentUser) {
      console.warn('🔍 DIAGNOSTIC: Cannot load conversations - no authenticated user');
      setLoading(false);
      setError('Please log in to view your messages');
      return;
    }
    const isMountedRef = {
      current: true
    };
    setLoading(true);
    setError(null);
    console.log('🔍 DIAGNOSTIC: Loading conversations for teacher:', currentUser.uid);
    // First, load conversations immediately (non-realtime)
    getUserConversations(currentUser.uid).then(initialConversations => {
      console.log(`Initial load: Found ${initialConversations.length} conversations`);
      // Filter out conversations with missing critical fields
      const validConversations = initialConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
      if (validConversations.length < initialConversations.length) {
        console.warn(`Filtered out ${initialConversations.length - validConversations.length} invalid conversations`);
      }
      // Sort conversations by lastMessageTimestamp (newest first)
      const sortedConversations = sortConversationsByTimestamp(validConversations);
      if (isMountedRef.current) {
        setConversations(sortedConversations);
        setLoading(false);
        // Don't set an error for empty conversations - it's a valid state
        if (error) setError(null);
        // Check which students have already been added to the teacher's roster
        if (currentUser && sortedConversations.length > 0) {
          checkStudentsAddedStatus(sortedConversations);
          // Set first conversation as selected by default
          if (!selectedConversation && sortedConversations.length > 0) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
        }
      }
    }).catch(err => {
      console.error('Error loading initial conversations:', err);
      if (isMountedRef.current) {
        setError('Failed to load conversations. Please try refreshing the page.');
        setLoading(false);
      }
    });
    // Then set up real-time listener for ongoing updates
    console.log('🔍 DIAGNOSTIC: Setting up conversation subscription for teacher:', currentUser.uid);
    const unsubscribe = subscribeToUserConversations(currentUser.uid, fetchedConversations => {
      console.log('🔍 DIAGNOSTIC: Received conversations update:', fetchedConversations.length);
      // Filter out conversations with missing critical fields
      const validConversations = fetchedConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
      // Sort conversations by lastMessageTimestamp (newest first)
      const sortedConversations = sortConversationsByTimestamp(validConversations);
      if (isMountedRef.current) {
        setConversations(sortedConversations);
        // Check which students have already been added
        if (currentUser) {
          checkStudentsAddedStatus(validConversations);
        }
        // Set first conversation as active if none is selected
        if (validConversations.length > 0 && !selectedConversation) {
          setSelectedConversation(validConversations[0]);
          activeConversationIdRef.current = validConversations[0].id;
        } else if (selectedConversation) {
          // Update the selected conversation if it's in the new list
          const updatedSelectedConversation = validConversations.find(conv => conv.id === selectedConversation.id);
          if (updatedSelectedConversation) {
            setSelectedConversation(updatedSelectedConversation);
          }
        }
        // Clear any error since we successfully got conversations
        if (error) setError(null);
        // Ensure loading is set to false
        if (loading) setLoading(false);
      }
    });
    return () => {
      console.log('🔍 DIAGNOSTIC: Cleaning up conversation subscription');
      isMountedRef.current = false;
      unsubscribe();
    };
  }, [currentUser]);
  // Helper function to sort conversations by timestamp
  const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime();
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e);
            return 0;
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime();
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp;
        }
        // Default case - use 0 to ensure consistent sorting
        return 0;
      };
      const timeA = getTimestampValue(a.lastMessageTimestamp);
      const timeB = getTimestampValue(b.lastMessageTimestamp);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });
  };
  // Check which students have already been added to the teacher's roster
  const checkStudentsAddedStatus = async (conversations: Conversation[]) => {
    if (!currentUser) return;
    try {
      const studentIds = conversations.map(conv => conv.studentId);
      const uniqueStudentIds = [...new Set(studentIds)];
      const statusMap: Record<string, boolean> = {};
      // Check each student's status
      const checkPromises = uniqueStudentIds.map(async studentId => {
        try {
          const isAdded = await isStudentAddedToTeacher(studentId, currentUser.uid);
          statusMap[studentId] = isAdded;
        } catch (err) {
          console.error(`Error checking if student ${studentId} is added:`, err);
          statusMap[studentId] = false;
        }
      });
      await Promise.all(checkPromises);
      setStudentsAddedMap(statusMap);
    } catch (error) {
      console.error('Error checking students added status:', error);
    }
  };
  // Subscribe to messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      activeConversationIdRef.current = null;
      return;
    }
    // Validate the selected conversation has all required fields
    if (!selectedConversation.id || !selectedConversation.teacherId || !selectedConversation.studentId) {
      console.error('Selected conversation is missing critical fields:', selectedConversation);
      setError('Invalid conversation data. Please try selecting another conversation.');
      return;
    }
    console.log(`🔥 REALTIME: Setting up message subscription for conversation: ${selectedConversation.id}`);
    activeConversationIdRef.current = selectedConversation.id;
    // Set up real-time listener for messages
    const unsubscribe = subscribeToConversationMessages(selectedConversation.id, fetchedMessages => {
      console.log(`🔥 REALTIME: Received ${fetchedMessages.length} messages for conversation ${selectedConversation.id}`, fetchedMessages);
      // Only update messages if this is still the active conversation
      if (activeConversationIdRef.current === selectedConversation.id) {
        console.log(`🔥 REALTIME: Updating messages state with ${fetchedMessages.length} messages for active conversation ${selectedConversation.id}`);
        // Sort messages by timestamp in ascending order (oldest first)
        const sortedMessages = [...fetchedMessages].sort((a, b) => {
          // Get timestamp values, properly handling Firestore timestamps
          const getTimestampValue = (timestamp: any): number => {
            // For Firestore timestamps with toDate method
            if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
              try {
                return timestamp.toDate().getTime();
              } catch (e) {
                console.error('Error converting Firestore timestamp:', e);
                return 0;
              }
            }
            // For Date objects
            if (timestamp instanceof Date) {
              return timestamp.getTime();
            }
            // For numeric timestamps
            if (typeof timestamp === 'number') {
              return timestamp;
            }
            // Default case - use 0 to ensure consistent sorting
            return 0;
          };
          const timeA = getTimestampValue(a.timestamp);
          const timeB = getTimestampValue(b.timestamp);
          // Sort in ascending order (oldest first)
          return timeA - timeB;
        });
        // Force React to recognize state change by creating a new array reference
        setMessages([...sortedMessages]);
        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
          }
        }, 50);
      } else {
        console.log(`🔥 REALTIME: Ignoring messages update for conversation ${selectedConversation.id} because active conversation is ${activeConversationIdRef.current}`);
      }
    });
    // Mark messages as read when conversation becomes active
    if (currentUser) {
      markMessagesAsRead(selectedConversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
    }
    return () => {
      console.log(`🔥 REALTIME: Unsubscribing from messages for conversation: ${selectedConversation.id}`);
      unsubscribe();
    };
  }, [selectedConversation?.id, currentUser?.uid]);
  // Handle selecting a conversation
  const handleSelectConversation = (conversation: Conversation) => {
    console.log(`🔥 REALTIME: Selecting conversation: ${conversation.id}`);
    // First unset the current conversation to ensure clean state
    setSelectedConversation(null);
    activeConversationIdRef.current = null;
    setMessages([]); // Clear messages when switching conversations
    // Then set the new conversation (this will trigger the useEffect)
    setTimeout(() => {
      setSelectedConversation(conversation);
      activeConversationIdRef.current = conversation.id;
      // Mark messages as read
      if (currentUser) {
        markMessagesAsRead(conversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
      }
    }, 0);
  };
  // Handle opening Add Student modal
  const handleOpenAddStudentModal = (conversation: Conversation) => {
    setSelectedStudent({
      id: conversation.studentId,
      name: conversation.studentName,
      avatar: conversation.studentAvatar,
      // Add email if available
      email: ''
    });
    setIsAddStudentModalOpen(true);
  };
  // Handle successful student addition
  const handleStudentAddSuccess = () => {
    if (selectedStudent && selectedStudent.id) {
      // Update local state to show student as added
      setStudentsAddedMap(prev => ({
        ...prev,
        [selectedStudent.id]: true
      }));
      // Show success message or update UI
      // For now, we'll just log it
      console.log(`Student ${selectedStudent.name} added successfully!`);
    }
  };
  // Handle sending a new message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event propagation to prevent any parent handlers
    if (!messageText.trim() || !selectedConversation || !currentUser) {
      console.log('Cannot send message - missing required data:', {
        hasMessageText: !!messageText.trim(),
        hasSelectedConversation: !!selectedConversation,
        hasCurrentUser: !!currentUser
      });
      return;
    }
    try {
      // Get receiver ID (student ID)
      const receiverId = selectedConversation.studentId;
      // Validate required fields
      if (!selectedConversation.id) {
        console.error('Missing conversation ID for message send');
        throw new Error('Missing conversation ID');
      }
      if (!currentUser.uid) {
        console.error('Missing sender ID (current user) for message send');
        throw new Error('Missing sender ID');
      }
      if (!receiverId) {
        console.error('Missing receiver ID (student) for message send');
        throw new Error('Missing receiver ID');
      }
      console.log('🔍 DIAGNOSTIC: Sending message:', {
        conversationId: selectedConversation.id,
        senderId: currentUser.uid,
        receiverId: receiverId,
        text: messageText
      });
      // Clear input immediately for better UX
      const messageToSend = messageText.trim();
      setMessageText('');
      try {
        // Send message to Firestore
        await sendMessage(selectedConversation.id, currentUser.uid, receiverId, messageToSend);
        console.log('Message sent successfully');
        // Use direct container scrolling instead of scrollIntoView
        setTimeout(() => {
          if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
          }
        }, 50);
      } catch (sendError) {
        console.error('Error in sendMessage function:', sendError);
        // Display a more specific error message
        const errorMessage = sendError instanceof Error ? `Failed to send message: ${sendError.message}` : 'Failed to send message. Please try again.';
        alert(errorMessage);
        throw sendError; // Re-throw to be caught by the outer catch
      }
    } catch (error) {
      console.error('🔍 DIAGNOSTIC: Error sending message:', error);
      // More detailed error logging
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        console.error('Error stack:', error.stack);
      }
      // Show error to user - already handled in the inner catch, but keep as fallback
      alert('Failed to send message. Please try again.');
    }
    return false; // Extra precaution against form submission
  };
  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation || !currentUser) return;
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    try {
      setIsUploading(true);
      // Determine file type
      let attachmentType: 'image' | 'pdf' | 'audio' | 'video' | 'doc' = 'doc';
      if (file.type.startsWith('image/')) attachmentType = 'image';else if (file.type.startsWith('audio/')) attachmentType = 'audio';else if (file.type.startsWith('video/')) attachmentType = 'video';else if (file.type === 'application/pdf') attachmentType = 'pdf';
      // Get receiver ID (student ID)
      const receiverId = selectedConversation.studentId;
      // Send message with attachment
      await sendMessage(selectedConversation.id, currentUser.uid, receiverId, '', {
        file,
        type: attachmentType
      });
      // Scroll to bottom
      setTimeout(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  // Trigger file input click
  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Filter conversations by search query
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    return conversation.studentName.toLowerCase().includes(searchQuery.toLowerCase());
  });
  // Safely format timestamp to prevent rendering issues
  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Just now';
    try {
      if (typeof timestamp === 'string') {
        return timestamp;
      }
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return 'Just now';
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Just now';
    }
  };
  // Render attachment based on type
  const renderAttachment = (message: Message) => {
    if (!message.attachmentUrl) return null;
    switch (message.attachmentType) {
      case 'image':
        return <div className="mt-2 max-w-full" data-id="element-8547">
            <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" data-id="element-8548">
              <img src={message.attachmentUrl} alt="Attachment" className="max-w-full rounded-lg max-h-60 object-contain" data-id="element-8549" />
            </a>
          </div>;
      case 'pdf':
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8550">
            <FileTextIcon className="h-6 w-6 mr-2 text-red-500" data-id="element-8551" />
            <span className="text-sm truncate" data-id="element-8552">
              {message.attachmentName || 'PDF Document'}
            </span>
          </a>;
      case 'audio':
        return <div className="mt-2" data-id="element-8553">
            <div className={`flex items-center p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8554">
              <MusicIcon className="h-6 w-6 mr-2 text-purple-500" data-id="element-8555" />
              <span className="text-sm truncate" data-id="element-8556">
                {message.attachmentName || 'Audio File'}
              </span>
            </div>
            <audio controls className="mt-2 max-w-full" data-id="element-8557">
              <source src={message.attachmentUrl} data-id="element-8558" />
              Your browser does not support audio playback.
            </audio>
          </div>;
      case 'video':
        return <div className="mt-2" data-id="element-8559">
            <video controls className="max-w-full rounded-lg max-h-60" data-id="element-8560">
              <source src={message.attachmentUrl} data-id="element-8561" />
              Your browser does not support video playback.
            </video>
          </div>;
      default:
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8562">
            <FileIcon className="h-6 w-6 mr-2 text-blue-500" data-id="element-8563" />
            <span className="text-sm truncate" data-id="element-8564">
              {message.attachmentName || 'Document'}
            </span>
          </a>;
    }
  };
  return <div className="w-full" data-id="element-8565">
      <div className="max-w-7xl mx-auto" data-id="element-8566">
        {/* Messages Interface */}
        <div className={`${isDark ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm overflow-hidden transition-colors duration-300`} data-id="element-8567">
          {loading ? <div className="flex items-center justify-center h-[calc(95vh-120px)]" data-id="element-8568">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-deep-pink" data-id="element-8569"></div>
            </div> : error ? <div className="flex items-center justify-center h-[calc(95vh-120px)]" data-id="element-8570">
              <div className="text-center max-w-md p-6" data-id="element-8571">
                <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8572" />
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-8573">
                  Error Loading Messages
                </h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-4`} data-id="element-8574">
                  {error}
                </p>
                <button onClick={() => window.location.reload()} className="bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg" data-id="element-8575">
                  Refresh Page
                </button>
              </div>
            </div> : <div className="flex h-[calc(95vh-120px)]" data-id="element-8576">
              {/* Conversations List */}
              <div className={`w-full md:w-1/3 border-r ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-8577">
                {/* Search */}
                <div className={`p-4 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-8578">
                  <div className={`relative rounded-lg ${isDark ? 'bg-navy-900/50' : 'bg-gray-100'}`} data-id="element-8579">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8580">
                      <SearchIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8581" />
                    </div>
                    <input type="text" placeholder="Search messages" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-deep-pink ${isDark ? 'bg-navy-900/50 text-white placeholder-gray-400' : 'bg-gray-100 text-gray-900 placeholder-gray-500'} border-none`} data-id="element-8582" />
                  </div>
                </div>
                {/* Conversations */}
                <div className="overflow-y-auto h-full pb-20" data-id="element-8583">
                  {filteredConversations.length > 0 ? filteredConversations.map(conversation => <div key={conversation.id} className={`p-4 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'} ${selectedConversation?.id === conversation.id ? isDark ? 'bg-navy-900/70' : 'bg-gray-100' : ''} hover:${isDark ? 'bg-navy-900/50' : 'bg-gray-50'} cursor-pointer transition-colors duration-200`} data-id="element-8584">
                        <div className="flex items-start" data-id="element-8585">
                          <div className="relative" data-id="element-8586">
                            {/* Student avatar/image */}
                            <div className="h-12 w-12 rounded-full overflow-hidden mr-3 cursor-pointer group relative" onClick={e => {
                      e.stopPropagation();
                      // Only open modal if student hasn't been added yet
                      if (!studentsAddedMap[conversation.studentId]) {
                        handleOpenAddStudentModal(conversation);
                      }
                    }} data-id="element-8587">
                              {/* Show "Add Student" tooltip on hover */}
                              {!studentsAddedMap[conversation.studentId] && <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100" data-id="element-8588">
                                  <UserPlusIcon className="h-6 w-6 text-white" data-id="element-8589" />
                                </div>}
                              {/* Student already added indicator */}
                              {studentsAddedMap[conversation.studentId] && <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-0.5 border-2 border-white dark:border-navy-800" data-id="element-8590">
                                  <CheckCircleIcon className="h-3 w-3 text-white" data-id="element-8591" />
                                </div>}
                              <img src={conversation.studentAvatar || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} alt={conversation.studentName} className="h-full w-full object-cover" onError={e => {
                        e.currentTarget.src = '';
                        e.currentTarget.parentElement.innerHTML = `<div class="h-12 w-12 ${isDark ? 'bg-navy-700' : 'bg-gray-200'} flex items-center justify-center"><UserIcon class="h-6 w-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}" /></div>`;
                      }} data-id="element-8592" />
                            </div>
                            {conversation.unread && <div className="absolute top-0 right-2 h-3 w-3 bg-deep-pink rounded-full" data-id="element-8593"></div>}
                          </div>
                          <div className="flex-1 min-w-0" onClick={() => handleSelectConversation(conversation)} data-id="element-8594">
                            <div className="flex justify-between" data-id="element-8595">
                              <div className="flex items-center" data-id="element-8596">
                                <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate cursor-pointer hover:underline`} onClick={e => {
                          e.stopPropagation();
                          // Only open modal if student hasn't been added yet
                          if (!studentsAddedMap[conversation.studentId]) {
                            handleOpenAddStudentModal(conversation);
                          }
                        }} data-id="element-8597">
                                  {conversation.studentName}
                                </h3>
                                {/* Add Student button */}
                                {!studentsAddedMap[conversation.studentId] && <button onClick={e => {
                          e.stopPropagation();
                          handleOpenAddStudentModal(conversation);
                        }} className={`ml-2 p-1 rounded-full ${isDark ? 'bg-deep-pink/10 text-deep-pink hover:bg-deep-pink/20' : 'bg-deep-pink/10 text-deep-pink hover:bg-deep-pink/20'}`} title="Add as student" data-id="element-8598">
                                    <UserPlusIcon className="h-3 w-3" data-id="element-8599" />
                                  </button>}
                                {/* Student already added indicator */}
                                {studentsAddedMap[conversation.studentId] && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-8600">
                                    Added
                                  </span>}
                              </div>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8601">
                                {formatTimestamp(conversation.lastMessageTimestamp)}
                              </span>
                            </div>
                            <p className={`text-xs truncate mt-1 ${conversation.unread ? isDark ? 'text-white font-medium' : 'text-gray-900 font-medium' : isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8602">
                              {conversation.lastMessage || 'Start a conversation'}
                            </p>
                          </div>
                        </div>
                      </div>) : <div className="flex flex-col items-center justify-center h-full p-8 text-center" data-id="element-8603">
                      <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8604" />
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8605">
                        No conversations yet
                      </h3>
                      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8606">
                        You don't have any messages yet. Students will appear
                        here when they message you.
                      </p>
                    </div>}
                </div>
              </div>
              {/* Message Content */}
              <div className="hidden md:flex md:w-2/3 flex-col" data-id="element-8607">
                {selectedConversation ? <>
                    {/* Conversation Header */}
                    <div className={`p-4 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'} flex items-center justify-between`} data-id="element-8608">
                      <div className="flex items-center" data-id="element-8609">
                        <div className="h-10 w-10 rounded-full overflow-hidden mr-3 cursor-pointer relative group" onClick={() => {
                    // Only open modal if student hasn't been added yet
                    if (!studentsAddedMap[selectedConversation.studentId]) {
                      handleOpenAddStudentModal(selectedConversation);
                    }
                  }} data-id="element-8610">
                          {/* Show "Add Student" tooltip on hover */}
                          {!studentsAddedMap[selectedConversation.studentId] && <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100" data-id="element-8611">
                              <UserPlusIcon className="h-5 w-5 text-white" data-id="element-8612" />
                            </div>}
                          {/* Student already added indicator */}
                          {studentsAddedMap[selectedConversation.studentId] && <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-0.5 border-2 border-white dark:border-navy-800" data-id="element-8613">
                              <CheckCircleIcon className="h-3 w-3 text-white" data-id="element-8614" />
                            </div>}
                          <img src={selectedConversation.studentAvatar || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} alt={selectedConversation.studentName} className="h-full w-full object-cover" onError={e => {
                      e.currentTarget.src = '';
                      e.currentTarget.parentElement.innerHTML = `<div class="h-10 w-10 ${isDark ? 'bg-navy-700' : 'bg-gray-200'} flex items-center justify-center"><UserIcon class="h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}" /></div>`;
                    }} data-id="element-8615" />
                        </div>
                        <div data-id="element-8616">
                          <div className="flex items-center" data-id="element-8617">
                            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} cursor-pointer hover:underline`} onClick={() => {
                        // Only open modal if student hasn't been added yet
                        if (!studentsAddedMap[selectedConversation.studentId]) {
                          handleOpenAddStudentModal(selectedConversation);
                        }
                      }} data-id="element-8618">
                              {selectedConversation.studentName}
                            </h3>
                            {/* Student already added indicator */}
                            {studentsAddedMap[selectedConversation.studentId] && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-8619">
                                Added
                              </span>}
                          </div>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8620">
                            Student
                          </p>
                        </div>
                      </div>
                      {/* Add Student button in header */}
                      {!studentsAddedMap[selectedConversation.studentId] && <button onClick={() => handleOpenAddStudentModal(selectedConversation)} className={`px-3 py-1 rounded-md text-sm flex items-center ${isDark ? 'bg-deep-pink/20 text-deep-pink hover:bg-deep-pink/30' : 'bg-deep-pink/10 text-deep-pink hover:bg-deep-pink/20'}`} data-id="element-8621">
                          <UserPlusIcon className="h-4 w-4 mr-1" data-id="element-8622" />
                          Add as Student
                        </button>}
                    </div>
                    {/* Messages */}
                    <div ref={messageContainerRef} className={`flex-1 overflow-y-auto p-4 ${isDark ? 'bg-navy-900/30' : 'bg-gray-50'}`} data-id="element-8623">
                      <div className="space-y-4" data-id="element-8624">
                        {messages.length > 0 ? messages.map(message => {
                    const isCurrentUser = message.senderId === currentUser?.uid;
                    return <div key={message.id} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-8625">
                                <div className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 ${isCurrentUser ? isDark ? 'bg-deep-pink text-white' : 'bg-deep-pink text-white' : isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} ${isCurrentUser ? '' : 'border'} ${isDark && !isCurrentUser ? 'border-navy-700' : !isDark && !isCurrentUser ? 'border-gray-200' : ''}`} data-id="element-8626">
                                  {message.text && <p className="text-sm" data-id="element-8627">{message.text}</p>}
                                  {message.attachmentUrl && renderAttachment(message)}
                                  <p className={`text-xs mt-1 ${isCurrentUser ? 'text-pink-100' : isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8628">
                                    {formatTimestamp(message.timestamp)}
                                  </p>
                                </div>
                              </div>;
                  }) : <div className="flex items-center justify-center h-full" data-id="element-8629">
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8630">
                              No messages yet. Start a conversation!
                            </p>
                          </div>}
                        {/* Add an empty div at the end to scroll to */}
                        <div ref={messageEndRef} data-id="element-8631" />
                      </div>
                    </div>
                    {/* Message Input */}
                    <div className={`p-4 border-t ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-8632">
                      <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-8633">
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.mp3,.mp4,.docx,.txt" data-id="element-8634" />
                        <button type="button" onClick={handleAttachmentClick} disabled={isUploading} className={`p-2 rounded-full ${isUploading ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-8635">
                          <PaperclipIcon className="h-5 w-5" data-id="element-8636" />
                        </button>
                        <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} placeholder={isUploading ? 'Uploading...' : 'Type a message...'} disabled={isUploading} className={`flex-1 mx-3 py-2 px-4 rounded-full focus:outline-none focus:ring-1 focus:ring-deep-pink ${isUploading ? 'opacity-70' : ''} ${isDark ? 'bg-navy-900/50 text-white placeholder-gray-400 border-navy-700' : 'bg-gray-100 text-gray-900 placeholder-gray-500 border-gray-200'} border`} data-id="element-8637" />
                        <button type="submit" disabled={!messageText.trim() || isUploading} className={`p-2 rounded-full ${messageText.trim() && !isUploading ? 'bg-deep-pink text-white' : isDark ? 'bg-navy-700 text-gray-400' : 'bg-gray-200 text-gray-500'} transition-colors`} data-id="element-8638">
                          <SendIcon className="h-5 w-5" data-id="element-8639" />
                        </button>
                      </form>
                    </div>
                  </> : <div className="flex-1 flex items-center justify-center" data-id="element-8640">
                    <div className="text-center" data-id="element-8641">
                      <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8642" />
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8643">
                        Select a conversation
                      </h3>
                      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8644">
                        Choose a student from the list to view your conversation
                      </p>
                    </div>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
      {/* Add Student Modal */}
      <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} student={selectedStudent} darkMode={isDark} onSuccess={handleStudentAddSuccess} data-id="element-8645" />
      <Footer data-id="element-8646" />
    </div>;
};